import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { invMovementService } from "../services/svc_invmovement";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest, ListRequestSchemaV2 } from "../types/common/commonTypes";

export const useTSQInvMovementFulfillmentList = (
  params: ListRequestSchemaV2,
  source: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.invmovement.listv2(params, source),
    queryFn: () => invMovementService.listFulfillments(params),
    enabled: enabled,
  });
};

export const useTSQInvMovementList = (params: ListRequest) => {
  return useQuery({
    queryKey: queryKeys.invmovement.list(params),
    queryFn: () => invMovementService.list(params),
    placeholderData: keepPreviousData,
  });
};

export const useTSQInvMovement = (id: string) => {
  return useQuery({
    queryKey: queryKeys.invmovement.detail(id),
    queryFn: () => {
      return invMovementService.getById(id);
    },
    enabled: !!id,
  });
};

export const useTSMCreateInvMovementBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => {
      return invMovementService.batchCreate(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.invmovement.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to create Inventory Movement Batch:", error);
    },
  });
};

export const useTSMCreateInvMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => {
      return invMovementService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.invmovement.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to create Status:", error);
    },
  });
};

export const useTSMUpdateInvMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => {
      return invMovementService.update(id, data);
    },
    onSuccess: (updatedMovement) => {
      queryClient.setQueryData(
        queryKeys.invmovement.detail(updatedMovement.id),
        updatedMovement,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.invmovement.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to update Movement:", error);
    },
  });
};

export const useTSMDeactivateInvMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: invMovementService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.invmovement.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to deactivate Movement:", error);
    },
  });
};

export const useTSMReactivateInvMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: invMovementService.reactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.invmovement.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to reactivate Movement:", error);
    },
  });
};
